import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { useState, useMemo, useEffect } from "react";
import Stepper from "@/components/common/stepper";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Step2 from "@/components/menu-management/special-menu/dish-the-day/add-dish-the-day/step2";
import Step1 from "@/components/menu-management/special-menu/dish-the-day/add-dish-the-day/step1";
import Step3 from "@/components/menu-management/special-menu/dish-the-day/add-dish-the-day/step3";
import { useFetchAllMenuData } from "@/hooks/use-fetch-all-menu-data";
import useAuth from "@/hooks/useAuth";
import CreateMenuSkeleton from "@/components/skeleton/create-menu-skeleton";
import StepFormButtons from "@/components/common/step-for-button";
import { getFirstErrorMessage } from "@/lib/utils";
import {
  createNewDishOfTheDay,
  clearDishOfTheDayMessages,
  updateExistingDishOfTheDay,
} from "@/redux/slice/menu-manage-slice/special-menu/dish-of-the-day-slice";
import { toast } from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { fetchAllDays } from "@/redux/slice/utility/day-slice";
import { useStepFormHandler } from "@/hooks/useStepFormHandler";

export const menu_id = 13;

function CreateDishOfDay() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { type } = useParams();
  const location = useLocation();
  const stepLabels = ["Step 1", "Step 2", "Step 3"];
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const item = location.state;

  useEffect(() => {
    dispatch(fetchAllDays());
  }, []);
  const fetchOnly = useMemo(() => ["addOns"], []);

  const { groupLoading } = useFetchAllMenuData(user.restaurant_id, menu_id, {
    fetchOnly,
  });

  const { createLoading, error, updateLoading } = useSelector(
    (state) => state.menuManage.dishTheDay
  );
  const { getLoading } = useSelector((state) => state.utility.day);

  const form = useForm({
    resolver: zodResolver(createDishOfDay),
    mode: "onChange",
    defaultValues: {
      type: "create",
      item_no: null,
      name: "",
      description: "",
      // category_id: "",
      vat: 0,
      status: "active",
      day_id: null,
      price: null,
      add_ons: [],
      file_manager_id: undefined,
    },
  });
  const errorMessage = getFirstErrorMessage(form);

  const { step, nextStep, setStep, prevStep, entityId, loading } =
    useStepFormHandler({
      type,
      entity: "Dish of the day",
      user,
      createAction: createNewDishOfTheDay,
      updateAction: updateExistingDishOfTheDay,
      totalSteps: stepLabels.length,
      item: item,
      toastSteps: { 0: false, 1: false, 2: true },
      successRedirectLink: "/backoffice/menu/special-menu/dish-the-day",
    });

  useEffect(() => {
    if (type !== "update") return;
    if (!item) {
      navigate("/backoffice/menu/special-menu/dish-the-day");
      return;
    }
    form.reset({
      type: "update",
      item_no: item?.item_no || null,
      name: item?.name || "",
      description: item?.description || "",
      // category_id: item.category_id,
      price: item?.price || null,
      day_id: item?.day?.id || null,
      vat: item.vat ?? 0,
      status: item?.status || "active",
      file_manager_id: item?.image?.id || undefined,
      add_ons: item?.add_ons?.map((a) => a.id) || [],
    });

    if (item?.image?.id && item?.image?.file_url != null) {
      setFiles([
        {
          file_manager_id: item?.image?.id,
          preview: item?.image?.file_url,
          name: item?.image?.name,
        },
      ]);
    }
  }, [type, item?.id, form]);

  if (groupLoading || getLoading) return <CreateMenuSkeleton />;

  return (
    <Layout2
      pageName={{
        title: `${
          type == "update"
            ? "Update Dish of the day"
            : "Add New Dish of the day"
        }`,
      }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Menu Management", path: "/backoffice/menu/drinks" },
        {
          name: "Special Menu",
          path: "/backoffice/menu/special-menu/dish-the-day",
        },
        {
          name: "Dish of the day",
          path: "/backoffice/menu/special-menu/dish-the-day",
        },
        {
          name: `${
            type == "update"
              ? "Update Dish of the day"
              : "Add New Dish of the day"
          }`,
          path: `/backoffice/menu/special-menu/${type}`,
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
    >
      <div className="py-5 container mt-5">
        <Stepper
          steps={stepLabels}
          onStepChange={setStep}
          type={form.watch("type")}
          currentStep={step}
        />
        {errorMessage && (
          <div className="text-cred mb-2 text-sm mt-4 flex justify-center items-center text-center">
            {errorMessage}
          </div>
        )}
        <Form {...form}>
          <form>
            {step === 0 && <Step1 menu_id={menu_id} form={form} />}
            {step === 1 && (
              <Step2
                item_id={entityId || item?.id}
                menu_id={menu_id}
                form={form}
              />
            )}
            {step === 2 && (
              <Step3 files={files} setFiles={setFiles} form={form} />
            )}

            <StepFormButtons
              step={step}
              totalSteps={stepLabels.length}
              isLoading={createLoading || updateLoading || loading}
              type={type}
              status={form.watch("status")}
              cancelLink="/backoffice/menu/special-menu/dish-the-day"
              onBack={prevStep}
              onNext={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form); // Step 1 & Step 2 → draft
              }}
              onSubmit={async () => {
                const data = form.getValues();
                await nextStep(data?.status, data, form); // Step 3 → active
              }}
              onDraftSave={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form, { draftButton: true });
              }}
            />
          </form>
        </Form>
      </div>
    </Layout2>
  );
}

export default CreateDishOfDay;

export const createDishOfDay = z.object({
  item_no: z
    .preprocess(
      (val) => {
        if (val === "" || val == null) return null;
        return Number(val);
      },
      z
        .number({
          invalid_type_error: "Item no must be a number",
        })
        .nullable()
    )
    .optional(),

  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  add_ons: z.array(z.number()).optional(),
  day_id: z.preprocess((val) => {
    if (val === "" || val == null) return undefined;
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  }, z.number().optional()),
  price: z.preprocess(
    (val) => (val === "" ? undefined : Number(val)),
    z
      .number({
        invalid_type_error: "Price must be a number",
      })
      .optional()
  ),
  vat: z
    .preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({
        invalid_type_error: "VAT percentage must be a number",
      })
    )
    .refine((val) => val >= 0, {
      message: "VAT percentage cannot be negative",
    })
    .refine((val) => val <= 100, {
      message: "VAT percentage cannot exceed 100",
    })
    .optional(),
  file_manager_id: z
    .number({
      required_error: "Please upload an image",
      invalid_type_error: "File ID must be a number",
    })
    .int("File ID must be an integer")
    .optional(),
  status: z.enum(["active", "inactive", "draft"]).default("active"),
});
